import './Ads.sass';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { adsActions, adsSelector } from '$store/ads';
import { Loader, MetaTags, Tooltip } from '$shared/components';
import {
  AnimatedEmoji,
  BottomBar,
  Button,
  Header,
  Placeholder,
  ScrollView,
  Tabs,
  TabsItem,
} from '$uikit';
import { useTranslator, useWikiLink } from '$hooks';
import { adsStatus } from '$utils/ads';
import { formatAmount } from '$utils/number';
import { goBack, showModal } from '$navigation/helper';
import { IcArrowDownCircle28, IcPlusCircle28, IcChevron16 } from '$assets';
import { AdStatus, CurrencyEnum } from '$store/models';
import { ModalLink } from '$shared/components/ModalLink/ModalLink';
import { getAdTitle } from './AdView';
import { ADS_BALANCE, NEW_AD } from '$shared/constants/modals';

export const Ads: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { loadingStatus, ads, balance, placeBalance } = useSelector(adsSelector);

  useEffect(() => {
    dispatch(adsActions.loadAds());
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(adsActions.loadAds());
  }, []);

  const handleCreateNewAd = useCallback(() => showModal(NEW_AD), []);

  const [curTab, setCurTab] = useState('All');
  const wikiLink = useWikiLink('/for-creators/advertising-platform');
  const adsFiltered = useMemo(
    () => (curTab === 'All' ? ads : ads.filter((ad) => adsStatus(ad.status) === curTab)),
    [ads, curTab],
  );

  if (loadingStatus === 'loading') return <Loader isWrapped />;
  if (loadingStatus === 'failed') {
    return (
      <Placeholder
        title={t('failed_title')}
        actions={
          <Button size="small" onClick={handleRetry}>
            {t('failed_button')}
          </Button>
        }
      >
        {t('failed_caption')}
      </Placeholder>
    );
  }

  const tooltip = (
    <Tooltip
      iconSize={24}
      title={t('ads_tooltip_title')}
      text={t('ads_tooltip_text')}
      action={
        <Button stretched iconRight={<IcChevron16 />} href={wikiLink} target="_blank">
          {t('ads_tooltip_open_support')}
        </Button>
      }
    />
  );

  return (
    <>
      <MetaTags title={t('ads_title')} />
      <Header
        showBackButton
        backButtonOnClick={() => goBack()}
        rightIcons={[{ icon: tooltip }]}
      >
        {t('ads_title')}
      </Header>
      <ScrollView>
        <div className="Ads__content">
          <div className="Ads__balance_wrap">
            <div className="Ads__balance_caption">{t('ads_balance_title')}</div>
            <div className="Ads__balances">
              <div className="Ads__balance_value">
                {formatAmount(balance, CurrencyEnum.TON)}
              </div>
              <div className="Ads__balance_value">
                {formatAmount(placeBalance, CurrencyEnum.PLACE)}
              </div>
            </div>
          </div>

          <div className="Ads__balance_actions">
            <ModalLink className="Ads__balance_action" name={ADS_BALANCE}>
              <IcArrowDownCircle28 />
              {t('ads_top_up')}
            </ModalLink>
          </div>

          {ads.length > 0 ? (
            <>
              <div className="Ads__title">{t('ads_list_title')}</div>
              <div className="Ads__tabs">
                <Tabs selected={curTab} onChange={setCurTab}>
                  <TabsItem value="All">All</TabsItem>
                  <TabsItem value={adsStatus(AdStatus.active)}>
                    {adsStatus(AdStatus.active)}
                  </TabsItem>
                  <TabsItem value={adsStatus(AdStatus.paused)}>
                    {adsStatus(AdStatus.paused)}
                  </TabsItem>
                  <TabsItem value={adsStatus(AdStatus.declined)}>
                    {adsStatus(AdStatus.declined)}
                  </TabsItem>
                  <TabsItem value={adsStatus(AdStatus.inReview)}>
                    {adsStatus(AdStatus.inReview)}
                  </TabsItem>
                </Tabs>
              </div>
              {adsFiltered.map((ad) => (
                <ModalLink name={`ad${ad.id}`} className="Ads__ad" key={ad.id}>
                  <div className="Ads__ad_line">
                    <div className="Ads__ad_title">{getAdTitle(ad)}</div>
                    <div className={`Ads__ad_status Ads__ad_status_${ad.status}`}>
                      {adsStatus(ad.status)}
                    </div>
                  </div>
                  <div className="Ads__ad_line">
                    <div className="Ads__ad_price">
                      {t('ad_bid')}{' '}
                      <span className="Ads__ad_price_value">
                        {formatAmount(ad.price, ad.currency || CurrencyEnum.TON)}
                      </span>
                    </div>
                    <div className="Ads__ad_conversions">
                      {t('ad_conversions')}:{' '}
                      <span className="Ads__ad_conversions_value">{ad.conversions}</span>
                    </div>
                  </div>
                </ModalLink>
              ))}
            </>
          ) : (
            <Placeholder
              centered
              className="Ads__list_placeholder"
              title={t('ads_list_placeholder_title')}
              icon={<AnimatedEmoji emoji="📣" />}
              iconSize={80}
              actions={
                <Button onClick={handleCreateNewAd} iconRight={<IcPlusCircle28 />}>
                  {t('ads_create_new')}
                </Button>
              }
            >
              {t('ads_list_placeholder_text')}
            </Placeholder>
          )}
        </div>
      </ScrollView>

      {ads.length > 0 && (
        <BottomBar>
          <Button onClick={handleCreateNewAd} iconRight={<IcPlusCircle28 />} stretched>
            {t('ads_create_new')}
          </Button>
        </BottomBar>
      )}
    </>
  );
};
